import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { axiosInstance } from '../../../helpers/api_helper';
import { ShadowBalanceAddType } from './types';
import {
  handleValidDate,
  handleValidTime,
  usdFormatter
} from '../../../helpers/helpers';
import { statusForNetwork } from '../DirectorInfo/slice';

export const getShadowRanking = createAsyncThunk(
  'shadowRanking/fetch',
  async (params: URLSearchParams) => {
    const response = await axiosInstance.get(
      '/user/mlm/shadow-properties/ranking',
      {
        params
      }
    );

    return response.data.data;
  }
);

export const addShadowBalance = createAsyncThunk(
  'shadowRanking/add',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        '/user/mlm/shadow-properties/ranking',
        data
      );
      toast.success('Shadow balance successfully added');

      return {
        userId: data.userId,
        ...response.data.data.next
      };
    } catch (error: any) {
      toast.error(error.response.data.error);
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const exportLicences = createAsyncThunk(
  'packageOrders/exportLicences',
  async (params: URLSearchParams) => {
    const response = await axiosInstance.get(`/licence/user/export`, {
      params
    });

    const dataForExport = response.data.data.userLicences.map((po: any) => ({
      licenceId: po?.id,
      userId: po?.user?.id,
      user: `${po?.user?.name} ${po?.user?.surname}`,
      type: licenceType(po?.orderType),
      amount: `${po?.price ? usdFormatter?.format(po?.price) : ''}`,
      paymentAddress: po?.paymentAddress || '',
      network: statusForNetwork(po?.networkType),
      orderDate: po?.dateCreated
        ? `${handleValidDate(po?.dateCreated)}, ${handleValidTime(
            po?.dateCreated
          )}`
        : '',
      paymentDate: po?.datePaid
        ? `${handleValidDate(po?.datePaid)}, ${handleValidTime(po?.datePaid)}`
        : '',

      endedDate: po?.dateEnd
        ? `${handleValidDate(po?.dateEnd)}, ${handleValidTime(po?.dateEnd)}`
        : '---',
      status: statusForTable(po?.status)
    }));

    return dataForExport;
  }
);

export const exportHeaders = [
  { label: 'Licence ID', key: 'licenceId' },
  { label: 'User ID', key: 'userId' },
  { label: 'User', key: 'user' },
  { label: 'Type', key: 'type' },
  { label: 'Amount', key: 'amount' },
  { label: 'Payment Address', key: 'paymentAddress' },
  { label: 'Network', key: 'network' },
  { label: 'Order Date', key: 'orderDate' },
  { label: 'Payment Date', key: 'paymentDate' },
  { label: 'BO Extended To', key: 'endedDate' },
  { label: 'Status', key: 'status' }
];

export const statusForTable = (status: number) => {
  switch (status) {
    case 1:
      return 'PENDING';
    case 2:
      return 'PAID';
    case 3:
      return 'CANCELED';
    case 4:
      return 'CANCELED BY USER';

    default:
      break;
  }
};

export const licenceType = (status: number) => {
  switch (status) {
    case 1:
      return 'ORDER';
    case 2:
      return 'PACKAGE';
    case 3:
      return 'LICENCE';
    case 4:
      return 'COMBO';

    default:
      break;
  }
};

export interface ShadowRankingStateInterface {
  data: any;
  loading: boolean;
  loadingExport: boolean;
  totalCount: number;
  dataForExport: [];
}

const initialState = {
  data: [],
  totalCount: 0,
  loading: false,
  loadingExport: false,
  dataForExport: []
} as ShadowRankingStateInterface;

const shadowRankingSlice = createSlice({
  name: 'shadowRanking',
  initialState,
  reducers: {
    removeLicencesForExport(state) {
      state.dataForExport = [];
    }
    // fill in primary logic here
  },
  extraReducers: (builder) => {
    builder.addCase(getShadowRanking.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getShadowRanking.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.data = payload.users;
      state.totalCount = payload.count;
    });
    builder.addCase(getShadowRanking.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(addShadowBalance.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addShadowBalance.fulfilled, (state, { payload }) => {
      state.loading = false;

      const index = state.data.findIndex(
        (p: any) => p.userId === payload.userId
      );

      state.data[index] = {
        ...payload,
        userId: state.data[index].userId,
        name: state.data[index].name,
        surname: state.data[index].surname,
        email: state.data[index].email
      };
      // state.data = payload.users;
      // state.totalCount = payload.count;
    });
    builder.addCase(addShadowBalance.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(exportLicences.pending, (state) => {
      state.loadingExport = true;
    });
    builder.addCase(exportLicences.fulfilled, (state, { payload }) => {
      state.loadingExport = false;
      state.dataForExport = payload;
    });
    builder.addCase(exportLicences.rejected, (state) => {
      state.loadingExport = false;
    });
  }
});

export const { removeLicencesForExport } = shadowRankingSlice.actions;

export default shadowRankingSlice.reducer;
